import { Helmet } from "react-helmet-async";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { 
  Search, 
  CheckCircle2, 
  Phone,
  ArrowLeft,
  ArrowRight,
  FileSearch,
  Users,
  BarChart3,
  Shield,
  FileText,
  Lightbulb
} from "lucide-react";
import { LucideIcon } from "lucide-react";

const iconMap: Record<string, LucideIcon> = {
  FileSearch,
  Users,
  BarChart3,
  Shield,
  FileText,
  Lightbulb
};

export default function IncidentInvestigation() {
  const { t, i18n } = useTranslation('services-pages');
  const isRTL = i18n.language === 'he';
  const ArrowIcon = isRTL ? ArrowLeft : ArrowRight;

  const processSteps = [
    {
      icon: "FileSearch",
      title: isRTL ? "איסוף עדויות ומידע" : "Evidence & Information Gathering",
      description: isRTL ? "תיעוד מקיף של מקום האירוע, ראיונות עדים ואיסוף כל המידע הרלוונטי" : "Comprehensive documentation of the incident site, witness interviews, and collection of all relevant information"
    },
    {
      icon: "BarChart3",
      title: isRTL ? "ניתוח גורמי שורש" : "Root Cause Analysis",
      description: isRTL ? "זיהוי הגורמים הישירים והעקיפים שהובילו לאירוע" : "Identification of direct and indirect factors that led to the incident"
    },
    {
      icon: "Lightbulb",
      title: isRTL ? "הפקת לקחים" : "Lessons Learned",
      description: isRTL ? "גיבוש מסקנות והמלצות למניעת הישנות" : "Formulating conclusions and recommendations to prevent recurrence"
    },
    {
      icon: "FileText",
      title: isRTL ? "דו\"ח תחקיר מקיף" : "Comprehensive Investigation Report",
      description: isRTL ? "דו\"ח מפורט הכולל ממצאים, ניתוח והמלצות" : "Detailed report including findings, analysis, and recommendations"
    },
    {
      icon: "Shield",
      title: isRTL ? "תכנית מניעה" : "Prevention Plan",
      description: isRTL ? "הכנת תכנית פעולה מעשית ליישום ההמלצות" : "Preparing a practical action plan for implementing recommendations"
    },
    {
      icon: "Users",
      title: isRTL ? "ליווי מול רגולציה" : "Regulatory Support",
      description: isRTL ? "סיוע מול משרד העבודה וגופים רגולטוריים" : "Assistance with the Ministry of Labor and regulatory bodies"
    }
  ];

  const investigationTypes = [
    {
      title: isRTL ? "תאונות עבודה" : "Work Accidents",
      items: [
        isRTL ? "תאונות עם נפגעים" : "Accidents with injuries",
        isRTL ? "תאונות קטלניות" : "Fatal accidents",
        isRTL ? "תאונות דרכים בעבודה" : "Work-related road accidents"
      ]
    },
    {
      title: isRTL ? "אירועי בטיחות" : "Safety Incidents",
      items: [
        isRTL ? "כמעט תאונות (Near Miss)" : "Near Miss events",
        isRTL ? "אירועי חומרים מסוכנים" : "Hazardous materials incidents",
        isRTL ? "כשלים בציוד" : "Equipment failures"
      ]
    },
    {
      title: isRTL ? "אירועי סביבה" : "Environmental Events",
      items: [
        isRTL ? "דליפות ושפיכות" : "Leaks and spills",
        isRTL ? "אירועי זיהום" : "Pollution events",
        isRTL ? "חריגות סביבתיות" : "Environmental violations"
      ]
    }
  ];

  return (
    <Layout>
      <Helmet>
        <title>{isRTL ? "תחקיר אירועי בטיחות ותאונות עבודה | אדם בטיחות" : "Safety Incident & Work Accident Investigation | Adam Safety"}</title>
        <meta name="description" content={isRTL ? "תחקירי בטיחות מקצועיים לאירועים ותאונות עבודה. ניתוח גורמי שורש והכנת תכנית מניעה." : "Professional safety investigations for incidents and work accidents. Root cause analysis and prevention plan preparation."} />
        <link rel="canonical" href="https://adam-safety.com/services/incident-investigation" />
        <meta property="og:title" content={isRTL ? "תחקיר אירועי בטיחות ותאונות עבודה | אדם בטיחות" : "Safety Incident & Work Accident Investigation | Adam Safety"} />
        <meta property="og:description" content={isRTL ? "תחקירי בטיחות מקצועיים לאירועים ותאונות עבודה. ניתוח גורמי שורש והכנת תכנית מניעה." : "Professional safety investigations for incidents and work accidents. Root cause analysis and prevention plan preparation."} />
        <meta property="og:url" content="https://adam-safety.com/services/incident-investigation" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={isRTL ? "תחקיר אירועי בטיחות ותאונות עבודה | אדם בטיחות" : "Safety Incident & Work Accident Investigation | Adam Safety"} />
        <meta name="twitter:description" content={isRTL ? "תחקירי בטיחות מקצועיים לאירועים ותאונות עבודה. ניתוח גורמי שורש והכנת תכנית מניעה." : "Professional safety investigations for incidents and work accidents. Root cause analysis and prevention plan preparation."} />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{ background: 'var(--gradient-hero)' }}
        >
          <div className="absolute inset-0 pattern-hero opacity-30" />
        </div>

        <div className="container relative z-10 px-4 py-20 md:py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/20 text-accent mb-6">
              <Search className="w-5 h-5" />
              <span className="text-sm font-medium">
                {isRTL ? "תחקירי בטיחות" : "Safety Investigations"}
              </span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6">
              {isRTL ? "תחקיר אירועי בטיחות ותאונות עבודה" : "Safety Incident & Work Accident Investigation"}
            </h1>
            
            <p className="text-xl md:text-2xl text-primary-foreground/80 mb-8 max-w-3xl mx-auto leading-relaxed">
              {isRTL 
                ? "למידה מאירועי העבר למניעת תאונות בעתיד. תחקירים מקצועיים שחושפים את הסיבות האמיתיות ומספקים פתרונות מעשיים."
                : "Learning from past events to prevent future accidents. Professional investigations that reveal the real causes and provide practical solutions."
              }
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button variant="cta" size="lg" className="gap-2 text-lg px-8">
                  <Phone className="w-5 h-5" />
                  {isRTL ? "להזמנת תחקיר" : "Request an Investigation"}
                </Button>
              </Link>
              <Link to="/services/safety-officer">
                <Button variant="outline" size="lg" className="gap-2 text-lg px-8 border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
                  {isRTL ? "שירותי בטיחות נוספים" : "More Safety Services"}
                  <ArrowIcon className="w-5 h-5" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="section-padding bg-background">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {isRTL ? "תהליך התחקיר" : "The Investigation Process"}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {isRTL 
                ? "מתודולוגיה מקצועית ומוכחת לחקירת אירועי בטיחות"
                : "Professional and proven methodology for safety incident investigation"
              }
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {processSteps.map((step, index) => {
              const IconComponent = iconMap[step.icon] || FileSearch;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="card-service"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="icon-container">
                      <IconComponent className="w-7 h-7" />
                    </div>
                    <span className="text-3xl font-bold text-accent/30">{index + 1}</span>
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-2">{step.title}</h3>
                  <p className="text-muted-foreground">{step.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Investigation Types */}
      <section className="section-padding section-alt">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {isRTL ? "סוגי אירועים שאנו מתחקרים" : "Types of Events We Investigate"}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {investigationTypes.map((type, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-card rounded-2xl p-6 border border-border"
              >
                <h3 className="text-xl font-bold text-foreground mb-4">{type.title}</h3>
                <ul className="space-y-3">
                  {type.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-primary">
        <div className="container px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-primary-foreground mb-4">
              {isRTL ? "התרחש אירוע? אנחנו כאן לעזור" : "Incident Occurred? We're Here to Help"}
            </h2>
            <p className="text-xl text-primary-foreground/80 mb-8">
              {isRTL 
                ? "צרו קשר לתחקיר מקצועי שיעזור למנוע אירועים דומים בעתיד"
                : "Contact us for a professional investigation that will help prevent similar incidents in the future"
              }
            </p>
            <Link to="/contact">
              <Button variant="cta" size="lg" className="gap-2 text-lg px-8">
                <Phone className="w-5 h-5" />
                {isRTL ? "צרו קשר עכשיו" : "Contact Us Now"}
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
